import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Button from '@mui/material/Button';
import useTranslation from '../../../../translations/use-translation';
import "./PersonalDetails.styles.scss";

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General / UR' },
  { value: 'ews',     label: 'EWS' },
  { value: 'obc',     label: 'OBC' },
  { value: 'scst',    label: 'SC / ST' },
];

const validate = (personal) => {
  const errors = {};
  if (!personal.fullName?.trim())  errors.fullName = 'Full name is required';
  if (!personal.mobile?.trim())    errors.mobile   = 'Mobile number is required';
  else if (!/^\d{10}$/.test(personal.mobile.trim())) errors.mobile = 'Enter a valid 10-digit mobile number';
  if (!personal.gender)            errors.gender   = 'Please select a gender';
  if (!personal.dob)               errors.dob      = 'Date of birth is required';
  if (!personal.category)          errors.category = 'Please select a category';
  return errors;
};

const PersonalDetails = ({ userProfile, setUserProfile }) => {
  const translate = useTranslation();
  const [focus, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(!!userProfile[0].personal.dob);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});

  const onFocus = () => setFocused(true);
  const onBlur  = () => setFocused(false);

  const handlePersonalDetailsForm = (e) => {
    const updated = {
      ...userProfile[0].personal,
      [e.target.name]: e.target.value,
    };
    setUserProfile([{
      personal: updated,
      contacts: { ...userProfile[0].contacts },
    }]);
    if (errors[e.target.name]) {
      setErrors((prev) => { const { [e.target.name]: _, ...rest } = prev; return rest; });
    }
  };

  const handleSave = () => {
    const errs = validate(userProfile[0].personal);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    try {
      const existing = JSON.parse(localStorage.getItem('userExtendedProfile') || '{}');
      localStorage.setItem('userExtendedProfile', JSON.stringify({
        ...existing,
        personal: userProfile[0].personal,
      }));
      setSaved(true);
      setErrors({});
      setTimeout(() => setSaved(false), 2500);
    } catch { /* storage full */ }
  };

  return (
    <div className="personal-details-container">
      <div className="form-container">
        <div className="person-form">
          <div className="form-title">Personal Information</div>

          <div className="form-fields">
            <div className="field-row">
              <div className="form-field">
                <label htmlFor="fullName">{translate('FULL_NAME')} <span className="personal-details__required">*</span></label>
                <TextField
                  name="fullName"
                  onChange={handlePersonalDetailsForm}
                  value={userProfile[0].personal.fullName}
                  id="fullName"
                  fullWidth
                  variant="outlined"
                  className="modern-input"
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                />
              </div>

              <div className="form-field">
                <label htmlFor="email">Email Address</label>
                <TextField
                  id="email"
                  name="email"
                  value={userProfile[0].personal.email}
                  fullWidth
                  variant="outlined"
                  className="modern-input modern-input--readonly"
                  InputProps={{ readOnly: true }}
                  helperText="From your account — manage in account settings"
                />
              </div>
            </div>

            <div className="field-row">
              <div className="form-field">
                <label htmlFor="mobile">{translate('MOBILE')} <span className="personal-details__required">*</span></label>
                <TextField
                  onChange={handlePersonalDetailsForm}
                  id="mobile"
                  name="mobile"
                  value={userProfile[0].personal.mobile}
                  type="tel"
                  fullWidth
                  variant="outlined"
                  className="modern-input"
                  error={!!errors.mobile}
                  helperText={errors.mobile}
                  inputProps={{ maxLength: 10 }}
                />
              </div>

              <div className="form-field">
                <label htmlFor="dob">{translate('DOB')} <span className="personal-details__required">*</span></label>
                <TextField
                  value={userProfile[0].personal.dob}
                  fullWidth
                  name="dob"
                  type={hasValue || focus ? 'date' : 'text'}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  onChange={(e) => {
                    handlePersonalDetailsForm(e);
                    setHasValue(!!e.target.value);
                  }}
                  variant="outlined"
                  className="modern-input"
                  error={!!errors.dob}
                  helperText={errors.dob}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>

            <div className="field-row single-column">
              <div className="form-field">
                <label>{translate('GENDER')} <span className="personal-details__required">*</span></label>
                <RadioGroup
                  aria-labelledby="gender-radio-group"
                  name="gender"
                  onChange={handlePersonalDetailsForm}
                  value={userProfile[0].personal.gender}
                  className="radio-group"
                >
                  <div className="radio-options">
                    <FormControlLabel control={<Radio className="radio-input" />} label={translate('MALE')}   value="male"   className="radio-option" />
                    <FormControlLabel control={<Radio className="radio-input" />} label={translate('FEMALE')} value="female" className="radio-option" />
                    <FormControlLabel control={<Radio className="radio-input" />} label={translate('OTHER')}  value="other"  className="radio-option" />
                  </div>
                </RadioGroup>
                {errors.gender && <FormHelperText error>{errors.gender}</FormHelperText>}
              </div>
            </div>

            <div className="field-row single-column">
              <div className="form-field">
                <label>Category <span className="personal-details__required">*</span></label>
                <RadioGroup
                  aria-labelledby="category-radio-group"
                  name="category"
                  onChange={handlePersonalDetailsForm}
                  value={userProfile[0].personal.category || ''}
                  className="radio-group"
                >
                  <div className="radio-options">
                    {CATEGORY_OPTIONS.map(({ value, label }) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio className="radio-input" />}
                        label={label}
                        className="radio-option"
                      />
                    ))}
                  </div>
                </RadioGroup>
                {errors.category && <FormHelperText error>{errors.category}</FormHelperText>}
                <p className="personal-details__category-hint">
                  Used to calculate applicable application fees during service applications
                </p>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button
              className="btn-save"
              variant="contained"
              onClick={handleSave}
            >
              {saved ? 'Saved!' : translate('SAVE')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
